import {
  Badge,
  Button,
  Flex,
  Heading,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { FaBullhorn, FaChevronUp, FaComments, FaQuestion } from "react-icons/fa";
import {
  BseQuestionDto,
  SourcingEventSupplierView,
  useCreateBseQuestionAsSupplierMutation,
  useCreateBseQuestionCommentAsSupplierMutation,
} from "../../../../autogen/bff-api";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { setNegotiationRoundQuestions, setQuestions } from "../../../../common/redux/reducers/basicBidReducer";
import { useQueryParams } from "../../../../common/useQueryParams";
import { Announcements } from "../../buyer/view-single/announcements/Announcements";
import { useNegotiation } from "./NegotiationContext";
import { Questions } from "./questions/Questions";

export const EventDialog = ({ event }: { event: SourcingEventSupplierView }) => {
  const dispatch = useAppDispatch();
  const params = useQueryParams();
  const [createBseQuestionAsSupplier] = useCreateBseQuestionAsSupplierMutation();
  const [createBseQuestionCommentAsSupplier] = useCreateBseQuestionCommentAsSupplierMutation();

  const [showDialog, setShowDialog] = useState(false);
  const [isSupplierQuestionsOpen, setIsSupplierQuestionsOpen] = useState(false);
  const [isAnnouncementsOpen, setIsAnnouncementsOpen] = useState(false);
  const { currentRound } = useNegotiation();

  const tabParam = params.get("tab");

  useEffect(() => {
    if (tabParam === "qa" && !isSupplierQuestionsOpen) {
      setIsSupplierQuestionsOpen(true);
    } else if (tabParam === "announcements" && !isAnnouncementsOpen) {
      setIsAnnouncementsOpen(true);
    } else if (!tabParam) {
      setIsSupplierQuestionsOpen(false);
      setIsAnnouncementsOpen(false);
    }
  }, [tabParam, isSupplierQuestionsOpen, isAnnouncementsOpen]);

  const getQuestionsForRoundOrThrow = (result: SourcingEventSupplierView, roundId: string): BseQuestionDto[] => {
    const updatedRound = result.negotiationRounds?.find((r) => r.id === roundId);
    if (!updatedRound) throw new Error("Missing updated round in response when negotiationRoundId was provided");
    return updatedRound.questions;
  };

  const getEventQuestions = (result: SourcingEventSupplierView): SourcingEventSupplierView["questions"] => {
    return result.questions;
  };

  const allQuestions = [
    ...(event.questions ?? []),
    ...((event.negotiationRounds?.flatMap((r) => r.questions ?? []) ?? []) as typeof event.questions),
  ];
  const unresolvedQuestionsCount = allQuestions.filter((q) => q.status === "Unresolved").length;

  const applyQuestionsUpdateFromResult = (result: SourcingEventSupplierView): void => {
    const roundId = currentRound?.id;
    if (roundId) {
      const updatedQuestions = getQuestionsForRoundOrThrow(result, roundId);
      dispatch(setNegotiationRoundQuestions({ negotiationRoundId: roundId, questions: updatedQuestions }));
      return;
    }
    dispatch(setQuestions(getEventQuestions(result)));
  };

  if (!event) return null;

  return (
    <>
      <Modal isOpen={isSupplierQuestionsOpen} onClose={() => params.clearBseDialogTab()} size="3xl">
        <ModalOverlay />
        <ModalContent
          width="full"
          height="80vh"
          maxHeight="80vh"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          p="5"
        >
          <ModalCloseButton />
          <ModalBody flex="1" minH="0" overflowY="auto" style={{ scrollbarGutter: "stable both-edges" }}>
            <Flex flexDirection="column" minH="0">
              <Heading as="h2" size="md" py="5">
                {t("Buyer Dialog")}
              </Heading>
              <Questions
                data={event}
                addComment={async (comment: string, questionId: string, negotiationRoundId?: string) => {
                  const result = await createBseQuestionCommentAsSupplier({
                    eventId: event.id,
                    questionId,
                    createBseQuestionCommentRequest: {
                      content: comment,
                      negotiationRoundId,
                    },
                  }).unwrap();
                  applyQuestionsUpdateFromResult(result);
                }}
                createNewQuestion={async (values: {
                  subject: string;
                  content: string;
                  announcementId: string | null;
                }) =>
                  createBseQuestionAsSupplier({
                    eventId: event.id,
                    createBseQuestionRequest: {
                      subject: values.subject,
                      content: values.content,
                      announcementId: values.announcementId ?? undefined,
                      negotiationRoundId: currentRound?.id,
                    },
                  })
                    .unwrap()
                    .then((result) => {
                      applyQuestionsUpdateFromResult(result);
                      return result;
                    })
                }
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isAnnouncementsOpen} onClose={() => params.clearBseDialogTab()} size={"3xl"}>
        <ModalOverlay />
        <ModalContent
          width="full"
          height="80vh"
          maxHeight="80vh"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          p="5"
        >
          <ModalCloseButton />
          <ModalBody flex="1" minH="0" overflowY="auto" style={{ scrollbarGutter: "stable both-edges" }}>
            <Flex flexDirection={"column"} minH="0">
              <Heading as="h2" size="md" pb="2">
                {t("Announcements")}
              </Heading>
              <Announcements
                canCreateAnnouncement={false}
                announcements={event.announcements}
                initialAnnouncementValues={null}
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Flex flexDirection="column" position="fixed" bottom="0" left="0" zIndex="10">
        <Menu size="lg" colorScheme="cyan" onOpen={() => setShowDialog(true)} onClose={() => setShowDialog(false)}>
          <MenuButton
            as={Button}
            position="relative"
            size="lg"
            roundedBottom="0"
            roundedLeft="0"
            border="1px solid"
            borderColor="smBorder"
            backgroundColor="smBackground"
            _hover={{ backgroundColor: "smBackgroundSecondary" }}
            shadow="lg"
            leftIcon={<Icon as={FaComments} />}
            rightIcon={<Icon boxSize="3" as={FaChevronUp} />}
          >
            {t("Event dialog")}{" "}
            {unresolvedQuestionsCount > 0 && !showDialog && (
              <Badge position="absolute" top="-2" right="-2" colorScheme="red" variant="solid" rounded="md">
                {unresolvedQuestionsCount} {t("unresolved!")}
              </Badge>
            )}
          </MenuButton>
          <MenuList>
            <MenuItem icon={<Icon as={FaQuestion} />} onClick={() => params.clearSelectedQuestion()}>
              <Flex alignItems="center" justifyContent="start">
                <Text>{t("Buyer Dialog")}</Text>
                {unresolvedQuestionsCount > 0 && (
                  <Badge colorScheme="red" variant="solid" rounded="md" ml="3">
                    {unresolvedQuestionsCount} {t("unresolved!")}
                  </Badge>
                )}
              </Flex>
            </MenuItem>
            <MenuItem icon={<Icon as={FaBullhorn} />} onClick={() => params.clearSelectedAnnouncement()}>
              {t("Announcements")}
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </>
  );
};
